// Micro-helpers DOM (pas de framework : zéro dépendance à maintenir).
// deno-lint-ignore-file no-explicit-any
export type Child = Node | string | number | null | undefined | false | Child[];

export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Record<string, any> | null = null,
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(props ?? {})) {
    if (v === false || v == null) continue;
    if (k.startsWith('on') && typeof v === 'function') {
      el.addEventListener(k.slice(2).toLowerCase(), v);
    } else if (k === 'class') el.className = v;
    else if (k === 'value') (el as any).value = v;
    else if (k === 'checked') (el as any).checked = Boolean(v);
    else if (k === 'html') el.innerHTML = v; // réservé au HTML produit par notre moteur de rendu
    else el.setAttribute(k, v === true ? '' : String(v));
  }
  append(el, children);
  return el;
}

function append(el: Node, children: Child[]): void {
  for (const c of children) {
    if (c == null || c === false) continue;
    if (Array.isArray(c)) append(el, c);
    else {el.appendChild(
        c instanceof Node ? c : document.createTextNode(String(c)),
      );}
  }
}

export function mount(parent: Element, ...children: Child[]): void {
  parent.replaceChildren();
  append(parent, children);
}

export function toast(message: string, kind: 'ok' | 'error' = 'ok'): void {
  const t = h('div', { class: `toast ${kind}` }, message);
  document.body.appendChild(t);
  setTimeout(() => t.remove(), kind === 'error' ? 8000 : 3500);
}

/** Exécute une action asynchrone en désactivant le bouton et en affichant les erreurs. */
export async function busy<T>(
  btn: HTMLButtonElement | null,
  fn: () => Promise<T>,
): Promise<T | undefined> {
  const label = btn?.textContent;
  if (btn) {
    btn.disabled = true;
    btn.textContent = '…';
  }
  try {
    return await fn();
  } catch (e) {
    toast(e instanceof Error ? e.message : String(e), 'error');
    return undefined;
  } finally {
    if (btn) {
      btn.disabled = false;
      btn.textContent = label ?? '';
    }
  }
}

export function field(
  label: string,
  input: HTMLElement,
  hint?: string,
): HTMLElement {
  return h(
    'label',
    { class: 'field' },
    h('span', { class: 'field-label' }, label),
    input,
    hint ? h('small', null, hint) : null,
  );
}

export const fmtDate = (iso: string | null | undefined): string =>
  iso
    ? new Date(iso).toLocaleString('fr-FR', {
      timeZone: 'Europe/Paris',
      dateStyle: 'medium',
      timeStyle: 'short',
    })
    : '—';

/** ISO -> valeur d'<input type="datetime-local"> dans le fuseau du navigateur. */
export function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export const lines = (s: string): string[] => s.split(/[\s,;]+/).map((x) => x.trim().toLowerCase()).filter(Boolean);
